/**
 * The ten mission types FLY SINGLE MISSION offers, and which of them this port
 * can honestly fly today.
 *
 * The labels are verbatim from the game's own two front-ends — `PS.EXE`'s
 * mission picker and `RBMB.EXE`'s Mission Builder — as transcribed in
 * docs/game/missions.md § *Fly Single Mission: the 10 mission types*. The
 * Mission Builder's wording is kept alongside `PS.EXE`'s because it is what
 * resolves the base game's duplicated "HUNT A ZEPPELIN" entry into two distinct
 * types.
 *
 * `MISSION_SUPPORT` is the honest half. A mission type is `"flyable"` only when
 * every system it needs actually exists in this port; everything else carries
 * the specific missing piece as prose, so the register doubles as the work
 * queue for the lanes that build those systems. (Same discipline as
 * `CALIBRATION_PENDING_TUNABLES` — an enumerable list a test can check, not a
 * `TODO` comment that rots.)
 */

export const MISSION_TYPES = [
  "dogfight",
  "patrol",
  "escortRecon",
  "escortBombers",
  "stopBombers",
  "destroyZeppelin",
  "interceptZeppelin",
  "balloonDefense",
  "balloonBusting",
  "scramble",
] as const;

export type MissionType = (typeof MISSION_TYPES)[number];

/** Verbatim `PS.EXE` FLY SINGLE MISSION labels. */
export const MISSION_TYPE_LABELS: Readonly<Record<MissionType, string>> = {
  dogfight: "DOGFIGHT",
  patrol: "PATROL THE FRONT",
  escortRecon: "ESCORT RECONNAISSANCE",
  escortBombers: "ESCORT A BOMBING RAID",
  stopBombers: "STOP A BOMBING RAID",
  destroyZeppelin: "HUNT A ZEPPELIN",
  interceptZeppelin: "HUNT A ZEPPELIN",
  balloonDefense: "BALLOON DEFENSE",
  balloonBusting: "BALLOON BUSTING!",
  scramble: "SCRAMBLE",
};

/** Verbatim `RBMB.EXE` Mission Builder labels — the disambiguating set. */
export const MISSION_BUILDER_LABELS: Readonly<Record<MissionType, string>> = {
  dogfight: "Dogfight",
  patrol: "Patrol",
  escortRecon: "Escort Reconnaisance",
  escortBombers: "Escort Bombers",
  stopBombers: "Stop Bombers",
  destroyZeppelin: "Destroy a Zeppelin",
  interceptZeppelin: "Intercept a Zeppelin",
  balloonDefense: "Balloon Defense",
  balloonBusting: "Balloon Busting",
  scramble: "Scramble",
};

export type MissionSupport =
  | { readonly state: "flyable" }
  | { readonly state: "deferred"; readonly missing: string };

const FLYABLE: MissionSupport = { state: "flyable" };

export const MISSION_SUPPORT: Readonly<Record<MissionType, MissionSupport>> = {
  dogfight: FLYABLE,
  patrol: FLYABLE,
  escortRecon: FLYABLE,
  escortBombers: FLYABLE,
  stopBombers: FLYABLE,
  destroyZeppelin: FLYABLE,
  interceptZeppelin: FLYABLE,
  balloonDefense: FLYABLE,
  balloonBusting: FLYABLE,
  scramble: FLYABLE,
};

export function isFlyable(type: MissionType): boolean {
  return MISSION_SUPPORT[type].state === "flyable";
}

/** The types this port can generate a real, decidable mission for. */
export const FLYABLE_MISSION_TYPES: readonly MissionType[] = MISSION_TYPES.filter(isFlyable);

/** The rest, with the specific missing system named. */
export const DEFERRED_MISSION_TYPES: readonly MissionType[] = MISSION_TYPES.filter((type) => !isFlyable(type));
